// Plato Medical API Sync Service
// Handles one-way sync from Plato Medical to Twin City Scheduler
// Uses Supabase Edge Function as proxy to avoid CORS issues

import { supabase } from '@/integrations/supabase/client';
import { createScheduleItem, Staff, ScheduleItem, getDoctors, getStaff, getScheduleItems } from './adminService';
import { assignDAToDoctor, type AssignmentResult } from './backupScheduleService';
import { addDaysInSingapore, parseDateString, formatDateToString } from '@/lib/dateUtils';
import { expandPlatoAppointmentsToRange } from '@/lib/platoRecurrence';
import type {
  PlatoAppointment,
  PlatoCalendar,
  PlatoDoctorMapping,
  PlatoSyncedAppointment,
  PlatoSyncResult,
  PlatoSyncResultItem,
  PlatoFetchAppointmentsParams,
  PlatoSyncPendingData,
  PlatoSyncProgressCallback,
} from '@/types/plato';

// =============================================================================
// Configuration
// =============================================================================

const PLATO_DATABASE = import.meta.env.VITE_PLATO_DATABASE || 'twincityendodontics';
// Note: API key is now stored in Edge Function secrets, not exposed to frontend

// Forum clinic ID (hardcoded for pilot)
const FORUM_CLINIC_ID = 'c1e1f1a1-1111-1111-1111-111111111111';

// =============================================================================
// Plato API Client
// =============================================================================

/**
 * Fetches appointments from Plato API via Supabase Edge Function proxy
 * This avoids CORS issues by having the server make the API call
 */
export const fetchPlatoAppointments = async (
  params: PlatoFetchAppointmentsParams
): Promise<PlatoAppointment[]> => {
  const { startDate, endDate, modifiedSince } = params;

  console.log('[platoService] Fetching appointments via Edge Function for:', startDate, 'to', endDate);

  // Call the Supabase Edge Function which proxies to Plato API
  const { data, error } = await supabase.functions.invoke('plato-sync', {
    body: {
      startDate,
      endDate,
      modifiedSince,
    },
  });

  if (error) {
    console.error('[platoService] Edge Function error:', error);
    throw new Error(`Plato sync error: ${error.message}`);
  }

  if (!data.success) {
    console.error('[platoService] Plato API error:', data.error);
    throw new Error(data.error || 'Failed to fetch appointments from Plato');
  }

  // Never let a partial pull proceed — a truncated fetch would make replace-mode
  // delete real appointments that simply weren't returned. The edge function sets
  // complete=false when it could not finish paginating (e.g. Plato rate limit);
  // throwing here makes the caller abort BEFORE any deletion, and the mutation
  // retry will try the whole sync again.
  if (data.complete === false) {
    throw new Error(
      data.error ||
        'Plato sync incomplete (likely rate limited before all pages loaded). Not applying a partial sync — please try again.',
    );
  }

  console.log('[platoService] Fetched', data.count, 'appointments from Plato');
  return data.data as PlatoAppointment[];
};

/**
 * Fetches calendars (doctors) from Plato API via Supabase Edge Function proxy
 */
export const fetchPlatoCalendars = async (): Promise<PlatoCalendar[]> => {
  const { data, error } = await supabase.functions.invoke('plato-sync', {
    body: {
      action: 'calendars',
    },
  });

  if (error) {
    console.error('[platoService] Edge Function error:', error);
    throw new Error(`Plato calendars error: ${error.message}`);
  }

  if (!data.success) {
    console.error('[platoService] Plato API error:', data.error);
    throw new Error(data.error || 'Failed to fetch calendars from Plato');
  }

  return data.data as PlatoCalendar[];
};

// =============================================================================
// Doctor Mapping
// =============================================================================

/**
 * Gets all active doctor mappings for the clinic
 */
export const getDoctorMappings = async (clinicId: string): Promise<PlatoDoctorMapping[]> => {
  const { data, error } = await supabase
    .from('plato_doctor_mapping')
    .select('*')
    .eq('twin_city_clinic_id', clinicId)
    .eq('is_active', true);

  if (error) {
    console.error('[platoService] Error fetching doctor mappings:', error);
    throw error;
  }

  return data as PlatoDoctorMapping[];
};

/**
 * Gets a Twin City doctor ID from a Plato calendar ID
 */
export const getTwinCityDoctorId = async (
  platoCalendarId: string,
  clinicId: string
): Promise<string | null> => {
  const { data, error } = await supabase
    .from('plato_doctor_mapping')
    .select('twin_city_doctor_id')
    .eq('plato_calendar_id', platoCalendarId)
    .eq('twin_city_clinic_id', clinicId)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No mapping found
      return null;
    }
    console.error('[platoService] Error fetching doctor mapping:', error);
    throw error;
  }

  return data?.twin_city_doctor_id || null;
};

/**
 * Creates or updates a doctor mapping
 */
export const upsertDoctorMapping = async (mapping: {
  platoCalendarId: string;
  platoDoctorName?: string;
  twinCityDoctorId: string;
  twinCityClinicId: string;
}): Promise<PlatoDoctorMapping> => {
  const { data, error } = await supabase
    .from('plato_doctor_mapping')
    .upsert({
      plato_calendar_id: mapping.platoCalendarId,
      plato_doctor_name: mapping.platoDoctorName,
      twin_city_doctor_id: mapping.twinCityDoctorId,
      twin_city_clinic_id: mapping.twinCityClinicId,
      is_active: true,
    }, {
      onConflict: 'plato_calendar_id',
    })
    .select()
    .single();

  if (error) {
    console.error('[platoService] Error upserting doctor mapping:', error);
    throw error;
  }

  return data as PlatoDoctorMapping;
};

/**
 * Deletes a doctor mapping by ID
 */
export const deleteDoctorMapping = async (mappingId: string): Promise<void> => {
  const { error } = await supabase
    .from('plato_doctor_mapping')
    .delete()
    .eq('id', mappingId);

  if (error) {
    console.error('[platoService] Error deleting doctor mapping:', error);
    throw error;
  }
};

/**
 * Gets all doctor mappings for a clinic (including inactive)
 */
export const getAllDoctorMappingsForClinic = async (clinicId: string): Promise<PlatoDoctorMapping[]> => {
  const { data, error } = await supabase
    .from('plato_doctor_mapping')
    .select('*')
    .eq('twin_city_clinic_id', clinicId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[platoService] Error fetching all doctor mappings:', error);
    throw error;
  }

  return data as PlatoDoctorMapping[];
};

// =============================================================================
// Synced Appointments Tracking
// =============================================================================

/**
 * Checks if an appointment has already been synced
 */
export const isAppointmentSynced = async (
  platoAppointmentId: string,
  platoDatabase: string = PLATO_DATABASE
): Promise<PlatoSyncedAppointment | null> => {
  const { data, error } = await supabase
    .from('plato_synced_appointments')
    .select('*')
    .eq('plato_appointment_id', platoAppointmentId)
    .eq('plato_database', platoDatabase)
    .maybeSingle();  // Returns null instead of error when no rows exist

  if (error) {
    console.error('[platoService] Error checking synced appointment:', error);
    throw error;
  }

  return data as unknown as PlatoSyncedAppointment | null;
};

/**
 * Records a synced appointment
 */
export const recordSyncedAppointment = async (record: {
  platoAppointmentId: string;
  platoDatabase?: string;
  scheduleItemId: string | null;
  platoModifiedAt?: string;
  platoData: PlatoAppointment;
  syncStatus: 'synced' | 'conflict' | 'error' | 'skipped';
  conflictReason?: string | null;
}): Promise<void> => {
  const { error } = await supabase
    .from('plato_synced_appointments')
    .upsert({
      plato_appointment_id: record.platoAppointmentId,
      plato_database: record.platoDatabase || PLATO_DATABASE,
      schedule_item_id: record.scheduleItemId,
      plato_modified_at: record.platoModifiedAt,
      plato_data: JSON.parse(JSON.stringify(record.platoData)),
      sync_status: record.syncStatus,
      conflict_reason: record.conflictReason ?? null,
    }, {
      onConflict: 'plato_appointment_id,plato_database',
    });

  if (error) {
    console.error('[platoService] Error recording synced appointment:', error);
    throw error;
  }
};

// =============================================================================
// Time Conversion Utilities
// =============================================================================

/**
 * Parses Plato datetime string to date and time components
 * Plato format: "YYYY-MM-DD HH:MM:SS"
 */
// Plato's real "modified" signal for change detection. The list API returns
// `last_edited` (e.g. "2026-07-02 14:23:54"); `modified_at` is usually absent,
// which is why plato_synced_appointments.plato_modified_at was always null.
const getPlatoModifiedAt = (appt: PlatoAppointment): string | undefined =>
  appt.modified_at || appt.last_edited || undefined;

const parsePlatoDateTime = (datetime: string): { date: string; time: string } => {
  const [datePart, timePart] = datetime.split(' ');
  return {
    date: datePart,
    time: timePart.substring(0, 5), // Get HH:MM only
  };
};

const PLATO_NON_OCCUPANCY_TITLE_PATTERNS = [
  /NOT\s+ON\s+DUTY/i,
  /\bNOT\s+IN\b/i,
  /^\s*\d{1,2}(?::\d{2})?\s*(AM|PM)?\s*TO\s*\d{1,2}(?::\d{2})?\s*(AM|PM)?\s*ONLY\s*$/i,
];

const isPlatoNonOccupancyPlaceholder = (title: string): boolean => {
  return PLATO_NON_OCCUPANCY_TITLE_PATTERNS.some(pattern => pattern.test(title.trim()));
};

/**
 * Checks if there's a time conflict for a doctor at the given time
 */
const hasTimeConflict = (
  doctorId: string,
  date: string,
  startTime: string,
  endTime: string,
  existingItems: ScheduleItem[]
): { hasConflict: boolean; conflictingItems: ScheduleItem[] } => {
  const conflictingItems = existingItems.filter(item =>
    item.doctorId === doctorId &&
    item.date === date &&
    // Time overlap check
    item.startTime < endTime &&
    item.endTime > startTime
  );

  return {
    hasConflict: conflictingItems.length > 0,
    conflictingItems,
  };
};

const appendNoteSection = (notes: string, section: string): string => {
  if (!section) {
    return notes;
  }
  return notes ? `${notes}\n\n${section}` : section;
};

const getStaffDisplayNameById = (staffId: string | undefined, staffList: Staff[]): string => {
  if (!staffId) {
    return 'Unassigned';
  }
  const staffMember = staffList.find(member => member.id === staffId);
  return staffMember?.short_name || staffMember?.name || 'Unknown DA';
};

const buildNurseDecisionSummary = (
  assignmentResult: AssignmentResult,
  staffList: Staff[]
): { noteLine: string; reportLine: string } => {
  const selectedRank = assignmentResult.assignment?.preferenceRank;
  const selectedStaffName = getStaffDisplayNameById(assignmentResult.assignment?.staffId, staffList);
  const roleLabel = selectedRank === 1
    ? 'primary nurse'
    : selectedRank === 2
      ? '1st backup nurse'
      : selectedRank === 3
        ? '2nd backup nurse'
        : selectedRank === 4
          ? '3rd backup nurse'
          : 'nurse';

  if (assignmentResult.reason) {
    return {
      noteLine: `[DA assignment] ${assignmentResult.reason}`,
      reportLine: assignmentResult.reason,
    };
  }

  if (selectedRank) {
    const fallbackMessage = selectedRank > 1
      ? ` via ${roleLabel}`
      : '';
    const message = `Assigned ${selectedStaffName} (Preference #${selectedRank})${fallbackMessage}.`;
    return {
      noteLine: `[DA assignment] ${message}`,
      reportLine: message,
    };
  }

  const fallbackSummary = assignmentResult.decisionTrail?.length
    ? assignmentResult.decisionTrail
        .map(decision => `#${decision.preferenceRank} ${decision.staffName}: ${decision.reason}`)
        .join('; ')
    : '';
  const message = fallbackSummary
    ? `No nurse assigned. ${fallbackSummary}.`
    : 'No nurse assigned.';

  return {
    noteLine: `[DA assignment] ${message}`,
    reportLine: message,
  };
};

// =============================================================================
// Main Sync Logic
// =============================================================================

/**
 * Syncs appointments from Plato for a given date range
 */
export const syncPlatoAppointments = async (
  startDate: string,
  endDate: string,
  clinicId: string = FORUM_CLINIC_ID,
  onProgress?: PlatoSyncProgressCallback,
): Promise<PlatoSyncResult> => {
  console.log('[platoService] Starting sync for date range:', startDate, 'to', endDate);

  const result: PlatoSyncResult = {
    success: true,
    totalProcessed: 0,
    added: 0,
    updated: 0,
    deleted: 0,
    skipped: 0,
    conflicts: 0,
    errors: 0,
    details: [],
  };

  try {
    // 1. Fetch appointments from Plato
    onProgress?.({ stage: 'fetching', message: 'Fetching appointments from Plato…' });
    const rawPlatoAppointments = await fetchPlatoAppointments({ startDate, endDate });
    console.log('[platoService] Fetched', rawPlatoAppointments.length, 'appointments from Plato');

    // Expand recurring masters into concrete occurrences within the sync range.
    // Plato returns a recurring series as one record dated to its ORIGINAL start
    // day; without this, recurring lunches / NOT-ON-DUTY / leave blocks are filed
    // on the wrong date and never appear on the day being synced.
    const platoAppointments = expandPlatoAppointmentsToRange(rawPlatoAppointments, startDate, endDate);
    console.log('[platoService] After recurrence expansion:', platoAppointments.length, 'occurrences in range', startDate, 'to', endDate);

    result.totalProcessed = platoAppointments.length;

    onProgress?.({ stage: 'loading', message: 'Loading schedule, doctors & staff…' });

    // 2. Load necessary data for auto-assignment
    const [doctors, staff, existingItemsRaw, doctorMappings] = await Promise.all([
      getDoctors(clinicId),
      getStaff(clinicId),
      getScheduleItems(clinicId, startDate, endDate),
      getDoctorMappings(clinicId),
    ]);

    // Make existingItems mutable for tracking
    let existingItems = [...existingItemsRaw];

    // Create a map of Plato calendar ID to Twin City doctor
    const calendarToDoctorMap = new Map<string, string>();
    doctorMappings.forEach(mapping => {
      calendarToDoctorMap.set(mapping.plato_calendar_id, mapping.twin_city_doctor_id);
    });

    // 3. Build a map of Plato appointments per Twin City doctor
    // Key: twinCityDoctorId, Value: Set of Plato appointment IDs
    const doctorPlatoApptIds = new Map<string, Set<string>>();
    doctorMappings.forEach(mapping => {
      doctorPlatoApptIds.set(mapping.twin_city_doctor_id, new Set());
    });
    for (const appt of platoAppointments) {
      const twinCityDoctorId = calendarToDoctorMap.get(appt.color);
      if (twinCityDoctorId) {
        if (!doctorPlatoApptIds.has(twinCityDoctorId)) {
          doctorPlatoApptIds.set(twinCityDoctorId, new Set());
        }
        doctorPlatoApptIds.get(twinCityDoctorId)!.add(appt.id);
      }
    }

    // Canonical row per Plato appointment = the one its sync record points to.
    // Used to de-duplicate correctly: when several rows share one external_id, we
    // keep the canonical (the row the loop will update) and delete the copies.
    const canonicalByExternalId = new Map<string, string>();
    {
      const platoIds = platoAppointments.map(a => a.id);
      const CHUNK = 100;
      for (let i = 0; i < platoIds.length; i += CHUNK) {
        const chunk = platoIds.slice(i, i + CHUNK);
        const { data: syncRecords, error: syncRecErr } = await supabase
          .from('plato_synced_appointments')
          .select('plato_appointment_id, schedule_item_id')
          .in('plato_appointment_id', chunk);
        if (syncRecErr) throw syncRecErr;
        (syncRecords ?? []).forEach(rec => {
          if (rec.schedule_item_id) {
            canonicalByExternalId.set(rec.plato_appointment_id, rec.schedule_item_id);
          }
        });
      }
    }

    // 4. Reconcile existing appointments for doctors that have Plato data.
    // "Replace mode" removes STALE PLATO appointments (previously synced but no
    // longer in Plato) and de-duplicates repeated Plato rows — but it now KEEPS
    // manually keyed appointments (external_id = null). Previously a manual entry
    // (e.g. a hand-keyed 9:30 review) was wiped on the next Sync because it had no
    // external_id; the clinic needs those to survive. Only type='appointment' is
    // touched; admin/break/leave/mc are untouched.
    const itemsToDelete: ScheduleItem[] = [];
    const seenDeleteIds = new Set<string>();
    // Duplicate rows we remove but whose SYNC RECORD must be kept (it points to
    // the canonical row we are keeping). Deleting the record would orphan it.
    const duplicateIds = new Set<string>();

    for (const [twinCityDoctorId, platoApptIds] of doctorPlatoApptIds) {
      const doctorPlatoItems = existingItems.filter(item =>
        item.doctorId === twinCityDoctorId &&
        item.type === 'appointment' &&
        !!item.external_id, // manual items (no external_id) are preserved
      );

      // (a) stale: previously-synced Plato items no longer present in Plato
      for (const item of doctorPlatoItems) {
        if (!platoApptIds.has(item.external_id!) && !seenDeleteIds.has(item.id)) {
          itemsToDelete.push(item);
          seenDeleteIds.add(item.id);
        }
      }

      // (b) duplicates: same external_id appearing more than once (a prior sync
      // bug created copies). Keep the CANONICAL row (the one the sync record
      // points to, which the loop below will update); if none is known, keep the
      // newest. Delete the rest so one Sync ends with exactly one row.
      const byExternalId = new Map<string, ScheduleItem[]>();
      for (const item of doctorPlatoItems) {
        if (!platoApptIds.has(item.external_id!)) continue; // stale handled above
        const list = byExternalId.get(item.external_id!) ?? [];
        list.push(item);
        byExternalId.set(item.external_id!, list);
      }
      for (const [externalId, dupes] of byExternalId.entries()) {
        if (dupes.length <= 1) continue;
        const canonicalId = canonicalByExternalId.get(externalId);
        const keep = dupes.find(d => d.id === canonicalId)
          ?? [...dupes].sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')))[0];
        dupes
          .filter(item => item.id !== keep.id)
          .forEach(item => {
            if (!seenDeleteIds.has(item.id)) {
              itemsToDelete.push(item);
              seenDeleteIds.add(item.id);
              duplicateIds.add(item.id); // keep its sync record (points to canonical)
            }
          });
      }
    }

    onProgress?.({
      stage: 'reconciling',
      message: itemsToDelete.length > 0
        ? `Removing ${itemsToDelete.length} stale/duplicate appointment(s)…`
        : 'Reconciling existing appointments…',
    });

    let removedCount = 0;
    for (const staleItem of itemsToDelete) {
      console.log(`[platoSync] Deleting appointment not in Plato: ${staleItem.id} (${staleItem.title}) - external_source: ${staleItem.external_source || 'manual'}`);
      const { error: deleteScheduleError } = await supabase
        .from('schedule_items')
        .delete()
        .eq('id', staleItem.id);
      if (deleteScheduleError) {
        throw deleteScheduleError;
      }

      // Clean up sync record ONLY for genuinely stale items. For duplicates we
      // keep the record — it points to the canonical row we are keeping.
      if (staleItem.external_id && !duplicateIds.has(staleItem.id)) {
        const { error: deleteSyncError } = await supabase
          .from('plato_synced_appointments')
          .delete()
          .eq('plato_appointment_id', staleItem.external_id);
        if (deleteSyncError) {
          throw deleteSyncError;
        }
      }

      removedCount += 1;
      result.details.push({
        platoAppointmentId: staleItem.external_id || staleItem.id,
        patientName: staleItem.title || 'Untitled appointment',
        status: 'removed',
        scheduleItemId: staleItem.id,
        reason: staleItem.external_id
          ? 'Removed because this Plato appointment is no longer present in synced data.'
          : 'Removed because this appointment is missing from the current Plato sync range.',
        timing: {
          date: staleItem.date,
          startTime: staleItem.startTime,
          endTime: staleItem.endTime,
        },
      });
    }
    result.deleted = removedCount;

    // Remove deleted items from existingItems for conflict checks
    existingItems = existingItems.filter(item => !itemsToDelete.some(s => s.id === item.id));

    // 5. Process each appointment
    let processedCount = 0;
    for (const platoAppt of platoAppointments) {
      processedCount++;
      onProgress?.({
        stage: 'importing',
        message: `Importing appointments… (${processedCount} of ${platoAppointments.length})`,
        current: processedCount,
        total: platoAppointments.length,
      });
      const resultItem: PlatoSyncResultItem = {
        platoAppointmentId: platoAppt.id,
        patientName: platoAppt.title,
        status: 'added',
      };

      try {
        // Parse Plato datetime first (needed for update logic)
        const startParsed = parsePlatoDateTime(platoAppt.starttime);
        const endParsed = parsePlatoDateTime(platoAppt.endtime);
        resultItem.timing = {
          date: startParsed.date,
          startTime: startParsed.time,
          endTime: endParsed.time,
        };

        // Build notes with patient name and original description
        let notes = '';
        if (platoAppt.title) {
          notes = `Patient: ${platoAppt.title}`;
        }
        if (platoAppt.description) {
          notes = notes ? `${notes}\n${platoAppt.description}` : platoAppt.description;
        }

        // Use description as title (primary), fall back to prefix, then 'Appointment'
        const appointmentTitle = platoAppt.description || platoAppt.prefix || 'Appointment';
        const isNonOccupancyPlaceholder = isPlatoNonOccupancyPlaceholder(appointmentTitle);

        // Get Twin City doctor from Plato calendar ID
        const twinCityDoctorId = calendarToDoctorMap.get(platoAppt.color);
        if (!twinCityDoctorId) {
          resultItem.status = 'skipped';
          resultItem.reason = `No doctor mapping for Plato calendar: ${platoAppt.color}`;
          result.skipped++;
          result.details.push(resultItem);

          await recordSyncedAppointment({
            platoAppointmentId: platoAppt.id,
            scheduleItemId: null,
            platoModifiedAt: getPlatoModifiedAt(platoAppt),
            platoData: platoAppt,
            syncStatus: 'skipped',
            conflictReason: resultItem.reason,
          });

          continue;
        }

        const doctor = doctors.find(d => d.id === twinCityDoctorId);
        if (!doctor) {
          resultItem.status = 'error';
          resultItem.reason = `Doctor not found: ${twinCityDoctorId}`;
          result.errors++;
          result.details.push(resultItem);
          continue;
        }

        // Check if this Plato appointment was already synced
        let existingScheduleItemId: string | null = null;
        let existingStaffId: string | null = null;
        const existingSyncRecord = await isAppointmentSynced(platoAppt.id);
        if (existingSyncRecord?.schedule_item_id) {
          // Verify the schedule item still exists — and capture its current DA
          // so that re-syncing does NOT reshuffle a nurse who is already assigned.
          const { data: existingItem, error: existingItemError } = await supabase
            .from('schedule_items')
            .select('id, staff_id')
            .eq('id', existingSyncRecord.schedule_item_id)
            .maybeSingle();
          if (existingItemError) {
            throw existingItemError;
          }

          if (existingItem?.id) {
            existingScheduleItemId = existingItem.id;
            existingStaffId = existingItem.staff_id ?? null;
            console.log(`[platoSync] Updating existing schedule item ${existingItem.id} for Plato appointment ${platoAppt.id}`);
          } else {
            console.log(`[platoSync] Schedule item ${existingSyncRecord.schedule_item_id} was deleted, recreating for Plato appointment ${platoAppt.id}`);
          }
        }

        const itemsForChecks = existingScheduleItemId
          ? existingItems.filter(item => item.id !== existingScheduleItemId)
          : existingItems;

        // Check for time conflicts
        const { hasConflict, conflictingItems } = hasTimeConflict(
          twinCityDoctorId,
          startParsed.date,
          startParsed.time,
          endParsed.time,
          itemsForChecks
        );

        // Assign the DA (nurse). Plato holds NO nurse data, so the DA is derived
        // locally. Once an appointment already has a DA — set by a prior sync or
        // manually by staff — sync must PRESERVE it. Re-deriving it on every sync
        // is what made DAs "jump" between staff (e.g. Liz → another DA on 13 Jul):
        // assignDAToDoctor is order- and availability-dependent, so an unchanged
        // Plato day could still reshuffle nurses on each press.
        let staffId: string | undefined = existingStaffId ?? undefined;
        let nurseDecision = { noteLine: '', reportLine: '' };

        if (existingStaffId) {
          const keptName = getStaffDisplayNameById(existingStaffId, staff);
          nurseDecision = {
            noteLine: `[DA assignment] Kept existing DA (${keptName}); not changed by Plato sync.`,
            reportLine: `Kept existing DA (${keptName}).`,
          };
          notes = appendNoteSection(notes, nurseDecision.noteLine);
        } else if (!isNonOccupancyPlaceholder) {
          const assignmentResult = await assignDAToDoctor(
            twinCityDoctorId,
            startParsed.time,
            startParsed.date,
            itemsForChecks,
            staff,
            endParsed.time
          );

          if (assignmentResult.assignment) {
            staffId = assignmentResult.assignment.staffId;
          }

          nurseDecision = buildNurseDecisionSummary(assignmentResult, staff);
          notes = appendNoteSection(notes, nurseDecision.noteLine);
        }

        // Auto-assign room using doctor's preferred room
        const roomId = isNonOccupancyPlaceholder ? undefined : doctor.preferred_room_id || undefined;

        // Track conflict info but still create the item
        // (User requested: "bring in ALL appointments from PLATO")
        let conflictReason = '';
        if (hasConflict) {
          conflictReason = `Time overlap with: ${conflictingItems.map(i => i.title || 'Untitled').join(', ')}`;
          // Add overlap info to notes so user can see it on the schedule
          notes = appendNoteSection(notes, `[${conflictReason}]`);
          result.conflicts++; // Track for informational purposes
          resultItem.status = 'conflict';
          resultItem.pendingData = {
            platoAppt,
            twinCityDoctorId,
            staffId,
            roomId,
            startParsed,
            endParsed,
            clinicId,
            notes,
          };
        }

        const resultReasons = [nurseDecision.reportLine];
        if (conflictReason) {
          resultReasons.push(conflictReason);
        }

        if (existingScheduleItemId) {
          const { error: updateExistingError } = await supabase
            .from('schedule_items')
            .update({
              doctor_id: twinCityDoctorId,
              staff_id: staffId || null,
              room_id: roomId || null,
              date: startParsed.date,
              start_time: startParsed.time,
              end_time: endParsed.time,
              type: 'appointment',
              title: appointmentTitle,
              notes,
              procedure: platoAppt.prefix || undefined,
              clinic_id: clinicId,
              external_source: 'plato',
              external_id: platoAppt.id,
            })
            .eq('id', existingScheduleItemId);
          if (updateExistingError) {
            throw updateExistingError;
          }

          await recordSyncedAppointment({
            platoAppointmentId: platoAppt.id,
            scheduleItemId: existingScheduleItemId,
            platoModifiedAt: getPlatoModifiedAt(platoAppt),
            platoData: platoAppt,
            syncStatus: 'synced',
            conflictReason: conflictReason || null,
          });

          if (resultItem.status !== 'conflict') {
            resultItem.status = 'updated';
          }
          resultItem.scheduleItemId = existingScheduleItemId;
          resultItem.reason = ['Updated with latest data', ...resultReasons].filter(Boolean).join(' | ');
          result.updated++;

          const updatedItem: ScheduleItem = {
            id: existingScheduleItemId,
            doctorId: twinCityDoctorId,
            staffId,
            roomId,
            startTime: startParsed.time,
            endTime: endParsed.time,
            date: startParsed.date,
            type: 'appointment',
            title: appointmentTitle,
            notes,
            procedure: platoAppt.prefix || undefined,
            clinic_id: clinicId,
            external_source: 'plato',
            external_id: platoAppt.id,
          };

          const existingIndex = existingItems.findIndex(item => item.id === existingScheduleItemId);
          if (existingIndex >= 0) {
            existingItems[existingIndex] = updatedItem;
          } else {
            existingItems.push(updatedItem);
          }

          result.details.push(resultItem);
          continue;
        }

        // Create schedule item (even if there was a conflict - user wants all synced)
        const scheduleItemData: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'> = {
          doctorId: twinCityDoctorId,
          staffId,
          roomId,
          startTime: startParsed.time,
          endTime: endParsed.time,
          date: startParsed.date,
          type: 'appointment',
          title: appointmentTitle, // Use description as title
          notes, // Patient name + description in notes
          procedure: platoAppt.prefix || undefined,
          clinic_id: clinicId,
          external_source: 'plato',
          external_id: platoAppt.id,
        };

        const createdItem = await createScheduleItem(scheduleItemData);
        resultItem.scheduleItemId = createdItem.id;
        if (resultItem.status !== 'conflict') {
          resultItem.status = 'added';
        }
        result.added++;
        resultItem.reason = resultReasons.filter(Boolean).join(' | ');

        await recordSyncedAppointment({
          platoAppointmentId: platoAppt.id,
          scheduleItemId: createdItem.id,
          platoModifiedAt: getPlatoModifiedAt(platoAppt),
          platoData: platoAppt,
          syncStatus: 'synced',
          conflictReason: conflictReason || null,
        });

        // Add the new item to existingItems for subsequent conflict checks
        existingItems.push(createdItem);

      } catch (error) {
        resultItem.status = 'error';
        resultItem.reason = error instanceof Error ? error.message : 'Unknown error';
        result.errors++;
      }

      result.details.push(resultItem);
    }

    onProgress?.({ stage: 'done', message: 'Sync complete.' });
    console.log('[platoService] Sync complete:', result);
    return result;

  } catch (error) {
    // Re-throw so the mutation's auto-heal retry can run (e.g. Plato rate limit).
    // Swallowing here would resolve as a silent no-op and skip the retry.
    console.error('[platoService] Sync failed:', error);
    onProgress?.({
      stage: 'error',
      message: error instanceof Error ? error.message : 'Sync failed.',
    });
    throw error;
  }
};

/**
 * Syncs appointments for a single day
 * The date should already be in Singapore timezone format (YYYY-MM-DD)
 */
export const syncPlatoAppointmentsForDay = async (
  date: string,
  clinicId: string = FORUM_CLINIC_ID,
  onProgress?: PlatoSyncProgressCallback,
): Promise<PlatoSyncResult> => {
  console.log('[platoService] Day sync for Singapore date:', date);
  return syncPlatoAppointments(date, date, clinicId, onProgress);
};

/**
 * Syncs appointments for a week (7 days starting from the given date)
 * Uses Singapore timezone for date calculations
 */
export const syncPlatoAppointmentsForWeek = async (
  startDate: string,
  clinicId: string = FORUM_CLINIC_ID,
  onProgress?: PlatoSyncProgressCallback,
): Promise<PlatoSyncResult> => {
  // Use Singapore timezone utilities for accurate date calculation
  const startDateSG = parseDateString(startDate);
  const endDateSG = addDaysInSingapore(startDateSG, 6);
  const endDate = formatDateToString(endDateSG);

  console.log('[platoService] Week sync using Singapore timezone - Start:', startDate, 'End:', endDate);

  return syncPlatoAppointments(startDate, endDate, clinicId, onProgress);
};

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Gets the Forum clinic ID from the database
 */
export const getForumClinicId = async (): Promise<string | null> => {
  const { data, error } = await supabase
    .from('clinics')
    .select('id')
    .eq('code', 'forum')
    .single();

  if (error) {
    console.error('[platoService] Error fetching Forum clinic:', error);
    return null;
  }

  return data?.id || null;
};

/**
 * Checks if Plato sync is configured and available
 * Always returns true since configuration is in Edge Function secrets
 */
export const isPlatoSyncAvailable = (): boolean => {
  return true; // API key is configured in Edge Function, not frontend
};

// =============================================================================
// Conflict Item Creation (Manual Approval)
// =============================================================================

/**
 * Creates a schedule item from pending conflict data after user approval
 * This is called when user manually approves a conflicting item
 * IDEMPOTENT: If schedule item already exists, updates it instead of creating duplicate
 */
export const createConflictItem = async (
  pendingData: PlatoSyncPendingData
): Promise<ScheduleItem> => {
  const {
    platoAppt,
    twinCityDoctorId,
    staffId,
    roomId,
    startParsed,
    endParsed,
    clinicId,
    notes,
  } = pendingData;

  // Check if this Plato appointment already has a schedule item
  const existingSyncRecord = await isAppointmentSynced(platoAppt.id);
  
  if (existingSyncRecord && existingSyncRecord.schedule_item_id) {
    // Check if the schedule item still exists
    const { data: existingItem, error: existingItemError } = await supabase
      .from('schedule_items')
      .select('*')
      .eq('id', existingSyncRecord.schedule_item_id)
      .maybeSingle();
    if (existingItemError) {
      throw existingItemError;
    }

    if (existingItem) {
      // Schedule item already exists - UPDATE it instead of creating duplicate
      console.log(`[platoSync] Conflict approval: Updating existing schedule item ${existingItem.id} instead of creating duplicate`);

      const notesWithConflictInfo = notes
        ? `${notes}\n\n[Updated with manual approval - time conflict acknowledged]`
        : '[Updated with manual approval - time conflict acknowledged]';

      const { error: updateScheduleError } = await supabase
        .from('schedule_items')
        .update({
          title: platoAppt.description || platoAppt.prefix || 'Appointment',
          notes: notesWithConflictInfo,
          start_time: startParsed.time,
          end_time: endParsed.time,
          procedure: platoAppt.prefix || undefined,
          doctor_id: twinCityDoctorId,
          staff_id: staffId || null,
          room_id: roomId || null,
        })
        .eq('id', existingSyncRecord.schedule_item_id);
      if (updateScheduleError) {
        throw updateScheduleError;
      }

      // Update sync record
      const { error: updateSyncError } = await supabase
        .from('plato_synced_appointments')
        .update({
          plato_modified_at: platoAppt.modified_at,
          plato_data: JSON.parse(JSON.stringify(platoAppt)),
          sync_status: 'synced',
          conflict_reason: 'Updated with manual approval after time conflict',
          updated_at: new Date().toISOString(),
        })
        .eq('plato_appointment_id', platoAppt.id);
      if (updateSyncError) {
        throw updateSyncError;
      }

      // Return the updated item
      const { data: updatedItem, error: fetchUpdatedError } = await supabase
        .from('schedule_items')
        .select('*')
        .eq('id', existingSyncRecord.schedule_item_id)
        .single();
      if (fetchUpdatedError) {
        throw fetchUpdatedError;
      }

      return updatedItem as unknown as ScheduleItem;
    }
  }

  // No existing schedule item - create a new one
  const notesWithConflictInfo = notes
    ? `${notes}\n\n[Created with manual approval - time conflict acknowledged]`
    : '[Created with manual approval - time conflict acknowledged]';

  const scheduleItemData: Omit<ScheduleItem, 'id' | 'created_at' | 'updated_at'> = {
    doctorId: twinCityDoctorId,
    staffId,
    roomId,
    startTime: startParsed.time,
    endTime: endParsed.time,
    date: startParsed.date,
    type: 'appointment',
    title: platoAppt.description || platoAppt.prefix || 'Appointment',
    notes: notesWithConflictInfo,
    procedure: platoAppt.prefix || undefined,
    clinic_id: clinicId,
    external_source: 'plato',
    external_id: platoAppt.id,
  };

  const createdItem = await createScheduleItem(scheduleItemData);

  // Record in plato_synced_appointments
  await recordSyncedAppointment({
    platoAppointmentId: platoAppt.id,
    scheduleItemId: createdItem.id,
    platoModifiedAt: getPlatoModifiedAt(platoAppt),
    platoData: platoAppt,
    syncStatus: 'synced',
    conflictReason: 'Created with manual approval after time conflict',
  });

  return createdItem;
};
